import type { ApiErrorBody, ErrorCode } from '@scan2eat/shared';
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    public readonly code: ErrorCode,
    public readonly httpStatus: number,
    message: string,
    public readonly details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function refresh(): Promise<string> {
  const res = await fetch(`${BASE_URL}/admin/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!res.ok) throw new ApiError('TOKEN_EXPIRED', 401, 'Refresh echoue');
  const data: { accessToken: string } = await res.json();
  accessToken = data.accessToken;
  return data.accessToken;
}

async function doRefresh(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = refresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body: ApiErrorBody = await res.json();
    return new ApiError(body.error.code, res.status, body.error.message, body.error.details);
  } catch {
    return new ApiError('INTERNAL_ERROR', res.status, `Erreur HTTP ${res.status}`);
  }
}

/**
 * Faut-il tenter un rafraichissement apres un 401 sur cette route ?
 *
 * Non pour les routes d'authentification elles-memes : un mot de passe errone
 * rend 401, et y repondre par un rafraichissement puis une redirection ferait
 * boucler la page de connexion sur elle-meme.
 */
function shouldAttemptRefresh(path: string): boolean {
  return !path.startsWith('/admin/auth/login') && !path.startsWith('/admin/auth/refresh');
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  let res = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });

  // Le rafraichissement ne depend pas du jeton en memoire, mais du cookie.
  // Exiger un jeton ici privait de session tout rechargement de page : le jeton
  // ne survit pas a un F5, alors que le cookie, lui, est toujours la.
  if (res.status === 401 && shouldAttemptRefresh(path)) {
    try {
      const newToken = await doRefresh();
      headers.set('Authorization', `Bearer ${newToken}`);
      res = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
    } catch {
      accessToken = null;
      // Ne pas rediriger si l'on y est deja : sans cette garde, une page de
      // connexion sans cookie se rechargerait indefiniment.
      if (window.location.pathname !== '/connexion') {
        window.location.href = '/connexion';
      }
      throw new ApiError('TOKEN_EXPIRED', 401, 'Session expiree');
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function apiFetchBlob(path: string): Promise<Blob> {
  const headers = new Headers();
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  let res = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' });

  if (res.status === 401 && accessToken) {
    try {
      const newToken = await doRefresh();
      headers.set('Authorization', `Bearer ${newToken}`);
      res = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' });
    } catch {
      accessToken = null;
      window.location.href = '/connexion';
      throw new ApiError('TOKEN_EXPIRED', 401, 'Session expiree');
    }
  }

  if (!res.ok) throw await parseError(res);
  return res.blob();
}
